import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import dayjs from "dayjs";
import { useAuth } from "../auth/AuthContext";
import { OrderVM } from "../data/types";
import { toOrderVM } from "../data/mappers";
import { formatCAD } from "../utils/currency";
import { fetchOrders } from "../data/api";
import StatusChip from "../components/StatusChip";
import OrdersListTable, { OrderCol } from "../orders/OrdersListTable";

const fmt = (d?: string) => (d ? dayjs(d).format("MMM D, YYYY") : "—");

/** Buyer "My Orders" as a dedicated, routable page (Oct-10 QA #2). */
const BuyerOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId, displayName, username } = useAuth();
  const [rows, setRows] = useState<OrderVM[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The backend already scopes orders to the signed-in buyer; this is a
      // defensive client filter so a buyer never sees another buyer's row.
      const all = (await fetchOrders() || []).map(toOrderVM);
      const name = (displayName || username || "").toLowerCase();
      const mine = all.filter((o) =>
        (userId != null && o.userId === userId) ||
        (!!name && (o.customer || "").toLowerCase() === name)
      );
      setRows((userId != null || name) ? mine : all);
    } finally { setLoading(false); }
  }, [userId, displayName, username]);

  useEffect(() => { load(); }, [load]);

  const columns: OrderCol[] = [
    { header: "Order #", render: (o) => <Typography variant="body2" fontWeight={600} color="primary">{o.orderId}</Typography> },
    { header: "Order date", render: (o) => fmt(o.orderDate) },
    { header: "Distributor", render: () => "Food Wholesale" },
    { header: "Items", align: "right", render: (o) => o.items },
    { header: "Total (CAD)", align: "right", render: (o) => formatCAD(o.total) },
    { header: "Requested delivery", render: (o) => fmt(o.deliveryDate) },
    { header: "Status", render: (o) => <StatusChip status={o.status} /> },
  ];

  return (
    <OrdersListTable
      title="My Orders"
      rows={rows}
      loading={loading}
      columns={columns}
      onOpen={(o) => navigate(`/orders/${o.id}`)}
      empty="You haven't placed any orders yet."
    />
  );
};

export default BuyerOrdersPage;
